import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { SiteConfigManager, useApplicationState } from '@atlas/core';

import LobbyAnyCategoriesBlock from './LobbyAnyCategoriesBlock';
import LobbyGameTopCategoriesBlock from './LobbyGameTopCategoriesBlock';
import LobbyLastWinnersBlock from './LobbyLastWinnersBlock';
import LobbyPrimaryBlock from './LobbyPrimaryBlock';
import LobbyPromotionsBlock from './LobbyPromotionsBlock';
import LobbyProvidersBlock from './LobbyProvidersBlock';
import LobbyRecentlyPlayedGamesBlock from './LobbyRecentlyPlayedGamesBlock';
import LobbySmarticoMissions from './LobbySmarticoMissions';
import LobbyTournamentsBlock from './LobbyTournamentsBlock';

const LOBBY_TYPES = {
  ANY_CATEGORIES: 'ANY_CATEGORIES',
  LAST_WINNERS: 'LAST_WINNERS',
  PRIMARY: 'PRIMARY',
  PROMOTIONS: 'PROMOTIONS',
  PROVIDERS: 'PROVIDERS',
  SMARTICO_MISSIONS: 'SMARTICO_MISSIONS',
  RECENTLY_PLAYED_GAMES: 'RECENTLY_PLAYED_GAMES',
  TOP_GAMES: 'TOP_GAMES',
  TOURNAMENTS: 'TOURNAMENTS',
};

const BLOCK_COMPONENTS = {
  [LOBBY_TYPES.TOP_GAMES]: LobbyGameTopCategoriesBlock,
  [LOBBY_TYPES.PRIMARY]: LobbyPrimaryBlock,
  [LOBBY_TYPES.LAST_WINNERS]: LobbyLastWinnersBlock,
  [LOBBY_TYPES.TOURNAMENTS]: LobbyTournamentsBlock,
  [LOBBY_TYPES.PROMOTIONS]: LobbyPromotionsBlock,
  [LOBBY_TYPES.PROVIDERS]: LobbyProvidersBlock,
  [LOBBY_TYPES.ANY_CATEGORIES]: LobbyAnyCategoriesBlock,
  [LOBBY_TYPES.SMARTICO_MISSIONS]: LobbySmarticoMissions,
  [LOBBY_TYPES.RECENTLY_PLAYED_GAMES]: LobbyRecentlyPlayedGamesBlock,
};

const LobbyBlock = ({ authenticated, block, onProviderClick, setActiveItem, source }) => {
  if (!block || typeof block !== 'object') return null;

  const { settings = {}, type } = block;
  const typeUpper = (type || '').toUpperCase();

  if (
    !authenticated &&
    [LOBBY_TYPES.TOURNAMENTS, LOBBY_TYPES.SMARTICO_MISSIONS].includes(typeUpper)
  ) {
    return null;
  }

  const Component = BLOCK_COMPONENTS[typeUpper];

  if (!Component) {
    // eslint-disable-next-line no-console
    console.warn(`Unknown Lobby Block type: ${type}`);
    return null;
  }

  if (typeUpper === LOBBY_TYPES.PROVIDERS) {
    return <Component {...settings} onProviderClick={onProviderClick} source={source} />;
  }

  if (typeUpper === LOBBY_TYPES.ANY_CATEGORIES) {
    return <Component {...settings} setActiveItem={setActiveItem} />;
  }

  return <Component {...settings} />;
};

LobbyBlock.propTypes = {
  authenticated: PropTypes.bool,
  block: PropTypes.object,
  onProviderClick: PropTypes.func,
  setActiveItem: PropTypes.func,
  source: PropTypes.string,
};

const sortLobbyStructure = lobbyStructure => {
  if (!lobbyStructure || !Array.isArray(lobbyStructure)) {
    return [];
  }

  return [...lobbyStructure].sort((a, b) => {
    const aOrder = a.orderPosition;
    const bOrder = b.orderPosition;

    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }
    return 0;
  });
};

const CasinoLobby = ({ onProviderClick, setActiveItem, source }) => {
  const { authenticated } = useApplicationState();

  const casinoLobbyConfig = SiteConfigManager.getConfig('casino_lobby_config', {});

  const sortedStructure = useMemo(() => {
    return sortLobbyStructure(casinoLobbyConfig?.lobbyStructure);
  }, [casinoLobbyConfig]);

  return (
    <div className="casino-lobby">
      {sortedStructure.map(block => (
        <LobbyBlock
          authenticated={authenticated}
          block={block}
          key={block.id}
          onProviderClick={onProviderClick}
          setActiveItem={setActiveItem}
          source={source}
        />
      ))}
    </div>
  );
};

CasinoLobby.propTypes = {
  onProviderClick: PropTypes.func,
  setActiveItem: PropTypes.func,
  source: PropTypes.string,
};

export default CasinoLobby;
